// Coordinator dispatch board — drag planned routes onto rig rows.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  assignLogisticsRouteToRig,
  listLogisticsCarrierRigs,
  type LogiCarrier,
  type LogiCarrierRig,
  type LogiRoute,
} from "@/lib/logistics";
import { carrierLabel, unitLabel } from "./logisticsRoutesShared";
import type { MfgUnit } from "@/lib/manufacturingClient";

export function useLogisticsRoutesDispatch(
  routes: LogiRoute[],
  carriers: LogiCarrier[],
  units: MfgUnit[],
  navTick: number,
  onAfterAssign: () => void
) {
  const [rigs, setRigs] = useState<LogiCarrierRig[]>([]);
  const [rigsLoading, setRigsLoading] = useState(false);
  const [assignBusyRigId, setAssignBusyRigId] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignWarnings, setAssignWarnings] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setRigsLoading(true);
    void (async () => {
      const res = await listLogisticsCarrierRigs({ is_active: true });
      if (cancelled) return;
      setRigsLoading(false);
      if (!res.ok) {
        setRigs([]);
        return;
      }
      const list = Array.isArray(res.data.rigs) ? res.data.rigs : [];
      setRigs(
        [...list].sort((a, b) => {
          const ca = carrierLabel(carriers, a.carrier_id);
          const cb = carrierLabel(carriers, b.carrier_id);
          const byCarrier = ca.localeCompare(cb);
          if (byCarrier !== 0) return byCarrier;
          return a.designation.localeCompare(b.designation);
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [navTick, carriers]);

  const plannedRoutes = useMemo(
    () => routes.filter((r) => r.route_state === "planned" && !r.rig_id),
    [routes]
  );

  const routesByRig = useMemo(() => {
    const map = new Map<string, LogiRoute[]>();
    for (const r of routes) {
      if (!r.rig_id) continue;
      const list = map.get(r.rig_id) ?? [];
      list.push(r);
      map.set(r.rig_id, list);
    }
    return map;
  }, [routes]);

  const routeChipLabel = useCallback(
    (route: LogiRoute) => unitLabel(units, route.unit_id),
    [units]
  );

  const assignRoute = useCallback(
    async (routeId: string, rigId: string) => {
      setAssignBusyRigId(rigId);
      setAssignError(null);
      setAssignWarnings([]);
      const res = await assignLogisticsRouteToRig({ route_id: routeId, rig_id: rigId });
      setAssignBusyRigId(null);
      if (!res.ok) {
        setAssignError(res.error);
        return;
      }
      setAssignWarnings(res.data.warnings ?? []);
      onAfterAssign();
    },
    [onAfterAssign]
  );

  const clearAssignFeedback = useCallback(() => {
    setAssignError(null);
    setAssignWarnings([]);
  }, []);

  return {
    rigs,
    rigsLoading,
    plannedRoutes,
    routesByRig,
    routeChipLabel,
    assignRoute,
    assignBusyRigId,
    assignError,
    assignWarnings,
    clearAssignFeedback,
  };
}
